import { isOriginTrusted } from '@de/config/allowed-data-sources';
import type { ActionDispatcher } from '@de/utils/action-resolver/ActionDispatcher';
import { ADVANCE_STEP_WITHOUT_SUBMIT_ACTION } from '@de/utils/action-resolver/hostSlotOutputActions';
import type { InterpolatedRequest } from '@de/utils/data-sources/fetchDataSource';
import { buildConversionRedirectUrl } from './buildConversionRedirectUrl';
import {
  createLeadSubmitHandler,
  type LeadSubmitDeps,
} from './leadSubmitHandler';
import { LEAD_SUBMIT_ACTION_TYPE } from './leadSubmitState';
import { requestFieldCheckReveal } from '@de/utils/binding-enhancer/hold-field-error';
import { newRelicNoticeError } from '@de/utils/newRelicActions';

/**
 * Single action handler closure. Handlers may return synchronously
 * or asynchronously; the dispatcher catches both sync throws and
 * async rejections so a misbehaving handler never breaks the
 * rendered step.
 */
export type ActionHandler = (
  params?: Readonly<Record<string, unknown>>,
) => void | Promise<void>;

/**
 * Outcome reported to the {@link ActionDispatcherDeps.onAfterDispatch}
 * post-dispatch hook. `ok: true` is fired after a successful
 * synchronous handler return AND after an async handler resolves;
 * `ok: false` is fired for sync throws and async rejections.
 */
export type ActionDispatchOutcome =
  | { readonly ok: true }
  | { readonly ok: false; readonly error: unknown };

/**
 * Host-injected dependencies for {@link createActionDispatcher}.
 *
 * Each SAD-canonical action type maps to one optional dep on this
 * record (mirroring DB-1.5's `NavigationDispatcherDeps` shape).
 * A handler is only registered when its dep is provided -- with two
 * deliberate exceptions documented inline:
 *
 *   - `comparison.toggle` is registered unconditionally as a
 *     warn-stub when `toggleComparison` is undefined, so DSL authors
 *     publishing `_action.onClick: { type: 'comparison.toggle' }`
 *     get a real wire-up the moment DB-2.2 lands instead of having
 *     to re-publish.
 *   - `navigation.externalLink` is registered when
 *     `externalLinkOrigins` is provided (even with an empty array,
 *     which is the deny-all default per env). The handler defaults
 *     `openExternalLink` to a `window.open` thunk so hosts don't
 *     need to inject one.
 */
export interface ActionDispatcherDeps {
  // ----------------------------------------------------------------
  // SAD-canonical handler deps
  // ----------------------------------------------------------------
  /** `navigation.advanceStep` -- already shipped under DB-1.5. */
  advanceStep: () => void | Promise<void>;
  /**
   * `navigation.advanceStepWithoutSubmit` -- advance to the next step, but do
   * nothing when the step would submit the flow or hand off to a redirect.
   * Bindings on a partner module's output run `navigation.advanceStep` as
   * this action. Absent, those bindings drop.
   */
  advanceStepWithoutSubmit?: () => void | Promise<void>;
  /**
   * `navigation.restartFlow` -- host clears flowState and jumps to
   * the entry step. Wired from `DynamicExperienceContext.handleRestart`.
   */
  restartFlow?: () => void;
  /**
   * `navigation.externalLink` -- defaults to a `window.open` thunk
   * with `noopener,noreferrer`. Hosts can override (e.g. tests, SSR
   * environments) by supplying their own. The origin check against
   * {@link ActionDispatcherDeps.externalLinkOrigins} runs *before*
   * this thunk fires, so this callback only sees URLs that already
   * passed the allowlist.
   */
  openExternalLink?: (url: string, target?: string) => void;
  /**
   * `submission.requestInfo` -- wraps the host's `useHandleSubmit`
   * callback so the dispatcher does not depend on the submission
   * hook directly (avoids inverting the React data flow). The
   * handler forwards `params` opaquely so DB-3.A can grow a schema
   * later (e.g. `{ result?: stepIdOverride }`).
   */
  submitRequestInfo?: (
    params?: Readonly<Record<string, unknown>>,
  ) => Promise<void>;
  /**
   * `submission.apiCall` -- wraps `fetchDataSource` (or any other
   * fetcher) with the bridge's security context (`trustedOrigins`,
   * `coerceRequestBody`). The result is dropped fire-and-forget;
   * future tracking lands via `onAfterDispatch`.
   */
  httpClient?: (req: InterpolatedRequest) => Promise<unknown>;
  /**
   * `results.openLeadFlow` -- the governed
   * intent action a DSL result card dispatches to open the platform's
   * lead-capture drawer/modal for the selected result. The DSL hands
   * off the selected repeater item verbatim via
   * `params.result: '{{item}}'`; this callback owns the drawer state,
   * PII/TCPA/PRQ surfaces, and lead submission (no conversion logic is
   * authored in DSL). Registered only when the host (`ConfigurableStep`
   * for a `type: 'result'` config) supplies it, so non-result steps
   * leave `dispatcher.has('results.openLeadFlow') === false` and the
   * binding enhancer drops the action with a single warn. The host
   * validates the `params.result` shape before forwarding (mirrors the
   * `toggleComparison` posture) so this factory stays presentation- and
   * domain-agnostic.
   */
  openLeadFlow?: (params?: Readonly<Record<string, unknown>>) => void;
  /**
   * `modal.open` / `modal.close` -- named hen.modal instance identity.
   * A CTA anywhere in the tree dispatches intent; the Modal that
   * registered `name` owns overlay state. Registered only when the
   * host supplies the callbacks (ConfigurableStep under ModalHostProvider).
   */
  openNamedModal?: (params?: Readonly<Record<string, unknown>>) => void;
  closeNamedModal?: (params?: Readonly<Record<string, unknown>>) => void;
  /**
   * `comparison.toggle` -- populated by DB-2.2 (EDUN-10690). Until
   * that lands, the handler logs a single `console.warn` per
   * dispatcher instance so authors notice the gap without breaking
   * the render.
   */
  toggleComparison?: (params?: Readonly<Record<string, unknown>>) => void;

  /**
   * `navigation.linkout` -- the generic, CMS-resolved linkout action.
   * Registered only when this dep is provided (mirrors the
   * `externalLinkOrigins` / `openLeadFlow` opt-in pattern), so a host that has
   * not wired tenant context leaves `dispatcher.has('navigation.linkout')`
   * false.
   *
   * The handler builds the same-origin BFF redirect URL
   * (`…/conversion-init?withRedirect=true&offeringId=…&tenantId=…`) from the
   * action params and opens it synchronously in a new tab; the BFF creates the
   * conversion server-side and 302s to the interpolated linkout URL. The
   * `tenantId` here is the host's authoritative tenant slug; an action param
   * `tenantId` may override it (e.g. cross-tenant authoring), but `offeringId`
   * must come from the params. `openLinkout` defaults to a `window.open` thunk
   * with `noopener,noreferrer`; hosts/tests can override it.
   */
  linkoutRedirect?: {
    tenantId: string;
    baseUrl?: string;
    /**
     * Flow type of the current step/flow (e.g. `direct-to-results`). Forwarded
     * to the BFF so campaign-management's conversion-time budget match uses the
     * same flow type the offerings `/search` did; a `scope:flowType`-restricted
     * budget is excluded when this is absent. An action param `flowType` may
     * override it.
     */
    flowType?: string;
    monetizationChannel?: string;
    /**
     * Step-configured traffic source for this surface. Forwarded to the BFF so
     * conversion-time budget matching uses the same value as offerings
     * `/search`. An action param `trafficSource` may override it.
     */
    trafficSource?: string;
    /**
     * Sources the live CMS v2 conversion `trackingContext` at click time
     * (Cohesion identity + `formatType` / `formatSubtype` monetization
     * classification), restoring Mobius parity so the Linkout / HLF GET
     * redirect carries the same identity the Voyager POST does. Called lazily
     * per dispatch with the action `params` so the host can fold in
     * param-supplied classification; a returned object is serialized into the
     * redirect URL and reconstructed on the BFF. Returns `undefined` when no
     * live context is resolvable, in which case the param is omitted and the
     * BFF falls back to the header-derived identity.
     */
    getTrackingContext?: (
      params?: Readonly<Record<string, unknown>>,
    ) => Record<string, unknown> | undefined;
    openLinkout?: (url: string) => void;
  };

  // ----------------------------------------------------------------
  // Trusted-origin set for navigation.externalLink URL validation.
  // Per child SAD §11 ("URL Validation: navigation.externalLink
  // validates URLs against CDN domain allowlist") and the DB-2.1
  // planning decision (5/5/26): a separate allowlist from DSM's
  // `trustedOrigins` so the two security policies are
  // independently auditable.
  // ----------------------------------------------------------------
  externalLinkOrigins?: readonly string[];

  // ----------------------------------------------------------------
  // Already-shipped (DB-1.5), non-SAD handlers -- kept for back-compat.
  // ----------------------------------------------------------------
  /** `navigation.goToStep` -- non-SAD, shipped. */
  goToStep?: (stepId: string, opts?: { resetFlowState?: boolean }) => void;
  /**
   * `navigation.goBack` -- non-SAD, shipped. The SAD does not have an
   * equivalent; existing DSLs that wired this in via DB-1.5 keep
   * working unchanged.
   */
  goBack?: () => void;
  /** `formState.clear` -- non-SAD, shipped (cascading-select reset). */
  clearFormState?: (keys: readonly string[]) => void;
  /**
   * `formState.set` -- shallow patch of formState keys (EDUN-17875).
   * The host writes the map in one shot so a source that reads several
   * of the patched keys refetches once against the merged snapshot.
   */
  setFormState?: (entries: Readonly<Record<string, string>>) => void;
  /**
   * Latest formState snapshot. `stub.simulate` reads the pending flag
   * when the delay ends and skips the follow-up when that flag is no
   * longer `"true"`, so a Cancel or Close action can clear it.
   */
  readFormState?: () => Readonly<Record<string, unknown>> | undefined;
  /**
   * `lead.submit` -- submits the enclosing `platform.form`'s lead to the v5
   * BFF route. Registered only with `setFormState` and `readFormState`,
   * which carry its status, busy and error keys.
   */
  leadSubmit?: LeadSubmitDeps;

  // ----------------------------------------------------------------
  // Extensibility seam (AC #4)
  // ----------------------------------------------------------------
  /**
   * Tenant- or app-specific handlers the host can wire in without
   * modifying core. Merged AFTER the built-ins so a same-named
   * extra handler overrides the built-in (and emits a single
   * `console.warn` per collision so the override is visible during
   * authoring).
   */
  extraHandlers?: Readonly<Record<string, ActionHandler>>;

  // ----------------------------------------------------------------
  // Tracking seam (DB-2.4 wires this; this ticket leaves it undefined
  // so the runtime path is a true no-op for tracking until then).
  // ----------------------------------------------------------------
  onAfterDispatch?: (
    type: string,
    params: Readonly<Record<string, unknown>> | undefined,
    outcome: ActionDispatchOutcome,
  ) => void;
}

/**
 * Build the production registry-backed {@link ActionDispatcher} that
 * `ConfigurableStep` installs into the provider stack (DB-2.1,
 * EDUN-10689). Replaces the S8-minimum
 * `createNavigationActionDispatcher`.
 *
 * Registry construction:
 *   - Each SAD-canonical handler is registered iff its dep is
 *     provided (mirrors today's `goToStep` / `goBack` /
 *     `clearFormState` pattern).
 *   - `navigation.externalLink` registers when `externalLinkOrigins`
 *     is provided (deny-all when empty); `openExternalLink` defaults
 *     to a `window.open` thunk.
 *   - `comparison.toggle` registers *unconditionally* -- the
 *     warn-stub keeps `dispatcher.has('comparison.toggle') === true`
 *     so authors get real wiring as soon as DB-2.2 supplies the dep.
 *   - `extraHandlers` merges last; collisions log a single warn-once
 *     to surface unintended overrides.
 *
 * Dispatch contract:
 *   - Unknown types log a single warn and short-circuit (matches
 *     `resolveActions.ts:107` expectation).
 *   - Sync throws and async rejections are caught and surfaced via
 *     `console.error` AND through the `onAfterDispatch` outcome.
 *   - `onAfterDispatch` is invoked exactly once per `dispatch()`
 *     call -- after the handler resolves (or rejects) so DB-2.4 can
 *     fire Tagular `FormSubmitted` / `ElementClicked` from a single
 *     hook instead of scattering tracking across handler files.
 */
export function createActionDispatcher(
  deps: ActionDispatcherDeps,
): ActionDispatcher {
  const handlers: Record<string, ActionHandler> = {};

  // ----- navigation.advanceStep (DB-1.5 carry-forward) -----
  // `advanceStep` is required, so the registration is unconditional.
  // The closure reads `deps.advanceStep` directly so a host that
  // swaps the callback at runtime is honored without rebuilding the
  // dispatcher (matches DB-1.5 semantics; today's hosts return
  // stable refs so this is defensive only).
  handlers['navigation.advanceStep'] = () => deps.advanceStep();

  // ----- navigation.advanceStepWithoutSubmit -----
  if (deps.advanceStepWithoutSubmit) {
    const advanceWithoutSubmit = deps.advanceStepWithoutSubmit;
    handlers[ADVANCE_STEP_WITHOUT_SUBMIT_ACTION] = () => advanceWithoutSubmit();
  }

  // ----- navigation.restartFlow (NEW) -----
  if (deps.restartFlow) {
    const restart = deps.restartFlow;
    handlers['navigation.restartFlow'] = () => {
      restart();
    };
  }

  // ----- navigation.externalLink (NEW) -----
  // Register whenever an origin allowlist was supplied -- even an
  // empty list means "the host is opting in to the security policy
  // but has not yet whitelisted any domain", which is a meaningfully
  // different state from "the host has not wired the feature at all"
  // and we want `dispatcher.has()` to reflect that.
  if (deps.externalLinkOrigins !== undefined) {
    const origins = deps.externalLinkOrigins;
    const opener = deps.openExternalLink ?? defaultOpenExternalLink;
    handlers['navigation.externalLink'] = (params) => {
      const url = params?.url;
      if (typeof url !== 'string' || url.length === 0) {
        // eslint-disable-next-line no-console
        console.warn(
          '[ActionDispatcher] navigation.externalLink called without a `url` param.',
        );
        return;
      }
      const target =
        typeof params?.target === 'string' && params.target.length > 0
          ? (params.target as string)
          : '_blank';
      // Reuse the bridge's existing origin primitive so the
      // navigation policy and the DSM fetch policy go through the
      // exact same parser / scheme guard. Path-only URLs are
      // accepted by `isOriginTrusted` (they cannot exfiltrate to a
      // foreign origin) but are not meaningful for an external-link
      // action; reject them explicitly so the warn is actionable.
      if (url.startsWith('/') || !isOriginTrusted(url, origins)) {
        // eslint-disable-next-line no-console
        console.warn(
          `[ActionDispatcher] navigation.externalLink blocked URL "${url}" -- not in externalLinkOrigins allowlist.`,
        );
        return;
      }
      opener(url, target);
    };
  }

  // ----- navigation.linkout -----
  // Registered only when the host wires tenant context. The handler validates
  // the required identity (`offeringId` from params, `tenantId` from the host
  // or a param override), builds the same-origin BFF redirect URL via the
  // shared helper, and opens it synchronously. Never throws -- missing identity
  // warns and no-ops, per the ActionDispatcher contract.
  if (deps.linkoutRedirect) {
    const {
      tenantId: hostTenantId,
      baseUrl,
      flowType: hostFlowType,
      monetizationChannel: hostMonetizationChannel,
      trafficSource: hostTrafficSource,
    } = deps.linkoutRedirect;
    const openLinkout = deps.linkoutRedirect.openLinkout ?? defaultOpenLinkout;
    const getTrackingContext = deps.linkoutRedirect.getTrackingContext;
    handlers['navigation.linkout'] = (params) => {
      const offeringId = params?.offeringId;
      if (typeof offeringId !== 'string' || offeringId.length === 0) {
        // eslint-disable-next-line no-console
        console.warn(
          '[ActionDispatcher] navigation.linkout called without an `offeringId` param.',
        );
        return;
      }
      const paramTenantId = params?.tenantId;
      const tenantId =
        typeof paramTenantId === 'string' && paramTenantId.length > 0
          ? paramTenantId
          : hostTenantId;
      // Source the live tracking context defensively: a misbehaving host
      // callback must never break the redirect (which still works from the
      // query identity + BFF header fallback).
      let trackingContext: Record<string, unknown> | undefined;
      if (getTrackingContext) {
        try {
          trackingContext = getTrackingContext(params);
        } catch {
          trackingContext = undefined;
        }
      }
      const url = buildConversionRedirectUrl({
        offeringId,
        tenantId,
        correlationId: stringParam(params?.correlationId),
        trafficSource: stringParam(params?.trafficSource) ?? hostTrafficSource,
        monetizationChannel:
          stringParam(params?.monetizationChannel) ?? hostMonetizationChannel,
        experience: stringParam(params?.experience),
        flowType: stringParam(params?.flowType) ?? hostFlowType,
        trackingContext,
        anonymousId: stringParam(params?.anonymousId),
        sessionId: stringParam(params?.sessionId),
        instanceId: stringParam(params?.instanceId),
        country: stringParam(params?.country),
        state: stringParam(params?.state),
        baseUrl,
      });
      if (!url) {
        // eslint-disable-next-line no-console
        console.warn(
          '[ActionDispatcher] navigation.linkout could not build a redirect URL -- missing offeringId or tenantId.',
        );
        return;
      }
      openLinkout(url);
    };
  }

  // ----- submission.requestInfo (NEW) -----
  if (deps.submitRequestInfo) {
    const submit = deps.submitRequestInfo;
    handlers['submission.requestInfo'] = (params) => submit(params);
  }

  // ----- submission.apiCall (NEW) -----
  if (deps.httpClient) {
    const httpClient = deps.httpClient;
    handlers['submission.apiCall'] = async (params) => {
      const endpoint = params?.endpoint;
      if (typeof endpoint !== 'string' || endpoint.length === 0) {
        // eslint-disable-next-line no-console
        console.warn(
          '[ActionDispatcher] submission.apiCall called without a string `endpoint` param.',
        );
        return;
      }
      const rawMethod = params?.method;
      const method =
        typeof rawMethod === 'string' && rawMethod.toUpperCase() === 'GET'
          ? 'GET'
          : 'POST';
      const headers = isPlainObject(params?.headers)
        ? (params.headers as Record<string, string>)
        : undefined;
      // `body` is intentionally `unknown` -- `fetchDataSource`
      // serializes through its existing JSON path. Authors can pass
      // an object literal or a pre-interpolated primitive; we do not
      // re-validate at this layer because DB-3.A will own publish-time
      // schema enforcement.
      const body = params?.body;
      const req: InterpolatedRequest = {
        endpoint,
        method,
        headers,
        body,
      };
      try {
        await httpClient(req);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn(
          `[ActionDispatcher] submission.apiCall to "${endpoint}" failed; DB-2.4 will route the outcome through onAfterDispatch.`,
          err,
        );
        // Re-throw so the dispatcher's catch-and-onAfterDispatch
        // pathway sees it -- the warn above is a developer-facing
        // breadcrumb, not a substitute for the structured outcome.
        throw err;
      }
    };
  }

  // ----- results.openLeadFlow -----
  // Registered only when the host wires a lead-flow entry point (a
  // `type: 'result'` config under `ConfigurableStep`). The closure
  // forwards `params` opaquely so the selected-result handoff
  // (`params.result`) and any future fields reach the governed runtime
  // unchanged; the host owns shape validation and the drawer/state
  // machine.
  if (deps.openLeadFlow) {
    const openLeadFlow = deps.openLeadFlow;
    handlers['results.openLeadFlow'] = (params) => openLeadFlow(params);
  }

  // ----- modal.open / modal.close -----
  // Named hen.modal identity. CTAs in other page regions dispatch
  // intent; the registered Modal owns open state. Missing/blank
  // `params.name` warns and no-ops, matching goToStep.
  if (deps.openNamedModal) {
    const openNamedModal = deps.openNamedModal;
    handlers['modal.open'] = (params) => openNamedModal(params);
  }
  if (deps.closeNamedModal) {
    const closeNamedModal = deps.closeNamedModal;
    handlers['modal.close'] = (params) => closeNamedModal(params);
  }

  // ----- comparison.toggle (NEW, warn-stub) -----
  // Registered unconditionally per plan §4.7. When the DB-2.2 dep
  // lands, swapping `toggleComparison` in at the call site is the
  // only change needed -- no dispatcher edit, no DSL re-publish.
  let comparisonStubWarned = false;
  handlers['comparison.toggle'] = (params) => {
    if (deps.toggleComparison) {
      deps.toggleComparison(params);
      return;
    }
    if (!comparisonStubWarned) {
      comparisonStubWarned = true;
      // eslint-disable-next-line no-console
      console.warn(
        '[ActionDispatcher] comparison.toggle dispatched but no ComparisonProvider is wired (DB-2.2 / EDUN-10690).',
      );
    }
  };

  // ----- navigation.goToStep (DB-1.5 carry-forward) -----
  if (deps.goToStep) {
    const goToStep = deps.goToStep;
    handlers['navigation.goToStep'] = (params) => {
      const stepId = params?.stepId;
      if (typeof stepId !== 'string' || stepId.length === 0) {
        // eslint-disable-next-line no-console
        console.warn(
          '[ActionDispatcher] navigation.goToStep called without a `stepId` param.',
        );
        return;
      }
      const resetFlowState = params?.resetFlowState;
      goToStep(
        stepId,
        typeof resetFlowState === 'boolean' ? { resetFlowState } : undefined,
      );
    };
  }

  // ----- navigation.goBack (DB-1.5 carry-forward; non-SAD) -----
  if (deps.goBack) {
    const goBack = deps.goBack;
    handlers['navigation.goBack'] = () => {
      goBack();
    };
  }

  // ----- formState.clear (DB-1.5 carry-forward; non-SAD) -----
  if (deps.clearFormState) {
    const clearFormState = deps.clearFormState;
    handlers['formState.clear'] = (params) => {
      // Strict-shape validation copied verbatim from
      // `createNavigationActionDispatcher` -- empty string and
      // non-string entries are dropped silently to handle the
      // `{{this.formKey}}` interpolation foot-gun (a component with
      // no formKey resolves to `[""]`).
      const rawKeys = params?.keys;
      if (!Array.isArray(rawKeys)) {
        // eslint-disable-next-line no-console
        console.warn(
          '[ActionDispatcher] formState.clear called without an array `keys` param; dropping.',
          params,
        );
        return;
      }
      const validKeys: string[] = [];
      for (let i = 0; i < rawKeys.length; i++) {
        const k = rawKeys[i];
        if (typeof k === 'string' && k.length > 0) validKeys.push(k);
      }
      if (validKeys.length === 0) return;
      clearFormState(validKeys);
    };
  }

  // ----- formState.set (EDUN-17875) -----
  if (deps.setFormState) {
    const setFormState = deps.setFormState;
    handlers['formState.set'] = (params) => {
      const rawValues = params?.values;
      if (!isPlainDataObject(rawValues)) {
        // eslint-disable-next-line no-console
        console.warn(
          '[ActionDispatcher] formState.set called without an object `values` param; dropping.',
          params,
        );
        return;
      }
      // Null prototype so a key that slips past the block list cannot
      // land on Object.prototype.
      const entries: Record<string, string> = Object.create(null);
      const skippedKeys: string[] = [];
      for (const key of Object.keys(rawValues)) {
        if (key.length === 0 || BLOCKED_FORM_STATE_KEYS.has(key)) continue;
        const coerced = coerceFormStateLeaf(rawValues[key]);
        if (coerced === undefined) {
          skippedKeys.push(key);
          continue;
        }
        entries[key] = coerced;
      }
      if (skippedKeys.length > 0) {
        // eslint-disable-next-line no-console
        console.warn(
          `[ActionDispatcher] formState.set skipped ${skippedKeys.join(', ')} because the value is an object, null, or undefined.`,
        );
      }
      if (Object.keys(entries).length === 0) return;
      setFormState(entries);
    };
  }

  // One run per pending key. Dispose and a later click both settle the
  // promise, and StrictMode can call dispose on the same dispatcher.
  const pendingRuns = new Map<
    string,
    { timer: ReturnType<typeof setTimeout>; resolve: () => void }
  >();
  const settlePending = (clearFlags: boolean): void => {
    if (pendingRuns.size === 0) return;
    const cleared: Record<string, string> = {};
    pendingRuns.forEach((run, key) => {
      clearTimeout(run.timer);
      run.resolve();
      if (clearFlags) cleared[key] = 'false';
    });
    pendingRuns.clear();
    if (clearFlags && deps.setFormState && Object.keys(cleared).length > 0) {
      deps.setFormState(cleared);
    }
  };

  const dispatcher: ActionDispatcher = {
    has(type: string): boolean {
      return type in handlers;
    },
    dispose(): void {
      settlePending(true);
    },
    cancelPendingSimulations(): void {
      settlePending(true);
    },
    dispatch(type: string, params?: Readonly<Record<string, unknown>>): void {
      const fn = handlers[type];
      if (!fn) {
        // eslint-disable-next-line no-console
        console.warn(
          `[ActionDispatcher] No handler registered for action "${type}".`,
        );
        return;
      }
      if (
        type === 'navigation.advanceStep' ||
        type === ADVANCE_STEP_WITHOUT_SUBMIT_ACTION
      ) {
        requestFieldCheckReveal();
      }
      try {
        const result = fn(params);
        if (isThenable(result)) {
          result.then(
            () => {
              deps.onAfterDispatch?.(type, params, { ok: true });
            },
            (err: unknown) => {
              newRelicNoticeError(
                err instanceof Error ? err : new Error(String(err)),
                {
                  source: 'ActionDispatcher',
                  action: type,
                  outcome: 'rejected',
                },
              );
              deps.onAfterDispatch?.(type, params, { ok: false, error: err });
            },
          );
          return;
        }
        deps.onAfterDispatch?.(type, params, { ok: true });
      } catch (err) {
        newRelicNoticeError(
          err instanceof Error ? err : new Error(String(err)),
          { source: 'ActionDispatcher', action: type, outcome: 'threw' },
        );
        deps.onAfterDispatch?.(type, params, { ok: false, error: err });
      }
    },
  };

  // Local simulation. Registered before extraHandlers so a host can
  // override it. `then` params are already resolved at click time.
  if (deps.setFormState) {
    const setFormState = deps.setFormState;
    handlers['stub.simulate'] = (params) =>
      simulateStubAction(params, {
        setFormState,
        readFormState: deps.readFormState,
        pendingRuns,
        getDispatcher: () => dispatcher,
      });
  }

  // ----- lead.submit -----
  if (deps.leadSubmit && deps.setFormState && deps.readFormState) {
    handlers[LEAD_SUBMIT_ACTION_TYPE] = createLeadSubmitHandler(
      deps.leadSubmit,
      {
        setFormState: deps.setFormState,
        readFormState: deps.readFormState,
        getDispatcher: () => dispatcher,
      },
    );
  }

  // ----- extraHandlers (extensibility seam) -----
  if (deps.extraHandlers) {
    let collisionWarned = false;
    for (const [type, handler] of Object.entries(deps.extraHandlers)) {
      if (type in handlers && !collisionWarned) {
        collisionWarned = true;
        // eslint-disable-next-line no-console
        console.warn(
          `[ActionDispatcher] extraHandlers override built-in handler(s) (first collision: "${type}").`,
        );
      }
      handlers[type] = handler;
    }
  }

  return dispatcher;
}

const BLOCKED_FORM_STATE_KEYS: ReadonlySet<string> = new Set([
  '__proto__',
  'constructor',
  'prototype',
]);

/**
 * Lead payload keys. A simulation must not write `true` / `false` over
 * one of these, or the next submit sends the flag instead of the answer.
 */
const LEAD_FORM_STATE_KEYS: ReadonlySet<string> = new Set([
  'firstName',
  'lastName',
  'email',
  'phoneNumber',
  'zip',
  'countryCode',
  'hsGradYear',
  'state',
  'city',
  'address',
  'levelOfEducation',
  'degree',
  'category',
  'subject',
  'militaryStatus',
  'nursingLicense',
  'teachingCredential',
  'startDate',
  'rnLicense',
  'enrollmentTimeline',
  'workExperience',
  'preferredContactMethod',
  'emailConsent',
  'birthDate',
  'gender',
  'gpa',
  'intendedStartDate',
  'bestTimeToContact',
  'currentOccupation',
  'employer',
  'campusPreference',
  'financialAid',
  'programFormat',
]);

/**
 * Same follow-ups publish and the panel allow. `navigation.advanceStep`
 * is excluded because the last step submits a lead. `tracking.fireEvent`
 * has no runtime handler, so it is excluded too.
 */
const STUB_FOLLOW_UP_TYPES: ReadonlySet<string> = new Set([
  'modal.open',
  'modal.close',
  'formState.clear',
  'formState.set',
  'navigation.goToStep',
  'navigation.goBack',
  'stub.simulate',
]);

/**
 * A data bag (`{}` or `Object.create(null)`). Stricter than
 * {@link isPlainObject}, which also accepts class instances — those report
 * `[object Object]` from `Object.prototype.toString`.
 */
function isPlainDataObject(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

/** Longest local simulation. Larger values are clamped so the button cannot stay busy for minutes. */
const MAX_STUB_DELAY_MS = 10_000;

interface StubFollowUp {
  type: string;
  params?: Record<string, unknown>;
}

interface PendingStubRun {
  timer: ReturnType<typeof setTimeout>;
  resolve: () => void;
}

interface StubSimulation {
  setFormState: (entries: Readonly<Record<string, string>>) => void;
  readFormState?: () => Readonly<Record<string, unknown>> | undefined;
  pendingRuns: Map<string, PendingStubRun>;
  getDispatcher: () => ActionDispatcher;
}

function readDelayMs(
  value: unknown,
): { delayMs: number; clamped: boolean } | null {
  if (value === undefined) return null;
  const numeric =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && value.trim() !== ''
        ? Number(value)
        : Number.NaN;
  if (!Number.isFinite(numeric) || numeric < 0) return null;
  if (numeric > MAX_STUB_DELAY_MS) {
    return { delayMs: MAX_STUB_DELAY_MS, clamped: true };
  }
  return { delayMs: numeric, clamped: false };
}

function readFollowUps(value: unknown): StubFollowUp[] | null {
  if (value === undefined) return null;
  const list = Array.isArray(value) ? value : [value];
  if (list.length === 0) return null;
  const actions: StubFollowUp[] = [];
  for (const entry of list) {
    if (
      !isPlainDataObject(entry) ||
      typeof entry.type !== 'string' ||
      entry.type.length === 0
    ) {
      return null;
    }
    if (entry.params !== undefined && !isPlainDataObject(entry.params)) {
      return null;
    }
    actions.push({
      type: entry.type,
      ...(entry.params !== undefined
        ? { params: entry.params as Record<string, unknown> }
        : {}),
    });
  }
  return actions;
}

/**
 * `stub.simulate` — busy flag, delay, then one action or a list.
 * A second call for the same key is ignored while that flag is still
 * true, so the follow-up opens once. A click after the flag was cleared
 * starts a new run. `then` values are the ones resolved at click time,
 * not when the timer fires. No network call.
 */
function simulateStubAction(
  params: Readonly<Record<string, unknown>> | undefined,
  simulation: StubSimulation,
): Promise<void> | void {
  const pendingKey =
    typeof params?.pendingKey === 'string' ? params.pendingKey.trim() : '';
  if (
    pendingKey.length === 0 ||
    BLOCKED_FORM_STATE_KEYS.has(pendingKey) ||
    LEAD_FORM_STATE_KEYS.has(pendingKey)
  ) {
    // eslint-disable-next-line no-console
    console.warn(
      `[ActionDispatcher] stub.simulate pendingKey "${pendingKey}" needs a non-empty name that is not a reserved name or a lead field; dropping.`,
    );
    return;
  }
  const delay = readDelayMs(params?.delayMs);
  if (delay === null) {
    // eslint-disable-next-line no-console
    console.warn(
      '[ActionDispatcher] stub.simulate called with a delayMs that is not a non-negative number; dropping.',
    );
    return;
  }
  if (delay.clamped) {
    // eslint-disable-next-line no-console
    console.warn(
      `[ActionDispatcher] stub.simulate delayMs is above ${MAX_STUB_DELAY_MS}ms; clamping.`,
    );
  }
  const followUps = readFollowUps(params?.then);
  if (followUps === null) {
    // eslint-disable-next-line no-console
    console.warn(
      '[ActionDispatcher] stub.simulate `then` must be an action or a non-empty list of actions; dropping.',
    );
    return;
  }
  const rejected = followUps.find(
    (action) => !STUB_FOLLOW_UP_TYPES.has(action.type),
  );
  if (rejected) {
    // eslint-disable-next-line no-console
    console.warn(
      `[ActionDispatcher] stub.simulate follow-up "${rejected.type}" is not allowed; dropping.`,
    );
    return;
  }

  const existing = simulation.pendingRuns.get(pendingKey);
  if (existing) {
    const snapshot = simulation.readFormState?.();
    const cancelled = snapshot !== undefined && snapshot[pendingKey] !== 'true';
    if (!cancelled) return;
    clearTimeout(existing.timer);
    simulation.pendingRuns.delete(pendingKey);
    existing.resolve();
  }

  simulation.setFormState({ [pendingKey]: 'true' });
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      simulation.pendingRuns.delete(pendingKey);
      const stillPending =
        simulation.readFormState === undefined ||
        simulation.readFormState()?.[pendingKey] === 'true';
      if (!stillPending) {
        resolve();
        return;
      }
      simulation.setFormState({ [pendingKey]: 'false' });
      const dispatcher = simulation.getDispatcher();
      for (const action of followUps) {
        if (dispatcher.has?.(action.type) === false) {
          // eslint-disable-next-line no-console
          console.warn(
            `[ActionDispatcher] stub.simulate follow-up "${action.type}" has no handler; dropping the rest of then.`,
          );
          break;
        }
        dispatcher.dispatch(action.type, action.params);
      }
      resolve();
    }, delay.delayMs);
    simulation.pendingRuns.set(pendingKey, { timer, resolve });
  });
}

/**
 * Turn one interpolated `values` leaf into the string `formState` stores.
 * `undefined` means skip the key (object, null, or undefined).
 */
function coerceFormStateLeaf(value: unknown): string | undefined {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    const parts: string[] = [];
    for (const entry of value) {
      if (typeof entry === 'string') {
        if (entry.length > 0) parts.push(entry);
      } else if (typeof entry === 'number' || typeof entry === 'boolean') {
        parts.push(String(entry));
      }
    }
    return parts.join(',');
  }
  return undefined;
}

function isThenable(value: unknown): value is Promise<unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { then?: unknown }).then === 'function'
  );
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Default opener used when the host does not inject one. Mirrors
 * `useHandleSubmit`'s `openResultUrl` posture: `noopener,noreferrer`
 * always, SSR-safe via `typeof window` guard. The opener is
 * intentionally simple -- the origin check has already passed by the
 * time this fires, so no further validation is needed here.
 */
function defaultOpenExternalLink(url: string, target?: string): void {
  if (typeof window === 'undefined') return;
  window.open(url, target ?? '_blank', 'noopener,noreferrer');
}

/**
 * Default opener for `navigation.linkout`. Mirrors
 * {@link defaultOpenExternalLink}: opens a new tab with `noopener,noreferrer`,
 * SSR-safe via the `typeof window` guard. The target is the BFF conversion
 * redirect URL built from the host-configured `linkoutRedirect.baseUrl` —
 * same-origin and relative by default, but optionally an absolute URL when a
 * host injects one. It is trusted by construction (host config + known identity
 * params, not user input), so no external-origin allowlist check is needed.
 */
function defaultOpenLinkout(url: string): void {
  if (typeof window === 'undefined') return;
  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * Narrow an action param to a non-empty string, else `undefined`. Used to feed
 * the optional attribution fields into the conversion-redirect builder without
 * forwarding non-string interpolation artifacts.
 */
function stringParam(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}
